import type { CompTopicExampleRow } from "@nexus/contracts";
import {
  Button,
  FACEBOOK_FEED_ASPECT_CLASS,
  MEDIA_BACKDROP_HOST_CLASS,
  MediaBackdrop,
  MediaPreview,
  Skeleton,
  cn,
} from "@nexus/ui";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatCompactNumber } from "../../lib/formatCompactNumber.js";

interface CompTopicDetailPreviewPanelProps {
  rows: CompTopicExampleRow[];
  selectedIndex: number;
  onSelectedIndexChange: (index: number) => void;
  isLoading: boolean;
}

const MEDIA_PREVIEW_CLASS = `${FACEBOOK_FEED_ASPECT_CLASS} w-full overflow-hidden rounded-lg`;

function formatDaysPerAsset(row: CompTopicExampleRow): string {
  if (row.daysPerAsset != null) return formatCompactNumber(row.daysPerAsset);
  if (row.assets <= 0) return "—";
  const ratio = row.days / row.assets;
  return Number.isFinite(ratio) ? formatCompactNumber(ratio) : "—";
}

export function CompTopicDetailPreviewPanel({
  rows,
  selectedIndex,
  onSelectedIndexChange,
  isLoading,
}: CompTopicDetailPreviewPanelProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3" data-testid="comp-topic-preview-skeleton">
        <Skeleton className={MEDIA_PREVIEW_CLASS} />
        <div className="flex flex-col gap-2 px-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
        </div>
        <Skeleton className="ml-auto h-8 w-20 rounded-md" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div
        className="flex min-h-[360px] items-center justify-center rounded-lg border border-dashed border-border bg-muted/20 p-6 text-sm text-muted-foreground"
        data-testid="comp-topic-preview-empty"
      >
        {t("overview.compTopicDetail.noPreview", {
          defaultValue: "No creatives match this topic and bucket.",
        })}
      </div>
    );
  }

  const safeIndex = Math.min(Math.max(0, selectedIndex), rows.length - 1);
  const row = rows[safeIndex]!;
  const isVideo = row.objectType.toLowerCase() === "video";
  const thumbnailSrc = row.thumbnailUrl || row.creativeUrl;
  const mediaSrc = isVideo ? row.creativeUrl : row.creativeUrl || row.thumbnailUrl;
  const canGoPrev = safeIndex > 0;
  const canGoNext = safeIndex < rows.length - 1;

  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-border bg-muted/10">
      <div className="p-4 pb-3">
        <MediaPreview
          src={mediaSrc || undefined}
          thumbnailSrc={thumbnailSrc}
          type={isVideo ? "video" : "image"}
          className={cn(MEDIA_PREVIEW_CLASS, MEDIA_BACKDROP_HOST_CLASS)}
        >
          {isVideo ? (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <span className="flex size-10 items-center justify-center rounded-full bg-black/40 text-white ring-1 ring-white/25 backdrop-blur-[2px]">
                <svg viewBox="0 0 24 24" fill="currentColor" className="size-5" aria-hidden="true">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </span>
            </div>
          ) : null}
          <MediaBackdrop src={thumbnailSrc} />
        </MediaPreview>
      </div>

      <div className="flex flex-col gap-2 px-4 pb-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground">
            {t("overview.compTopicDetail.metricDaysAssets", { defaultValue: "Days/Assets" })}
          </span>
          <span className="text-xs font-medium tabular-nums text-foreground">
            {formatDaysPerAsset(row)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground">
            {t("overview.compTopicDetail.columnUsage", { defaultValue: "Usage" })}
          </span>
          <span className="text-xs font-medium text-foreground">
            {t("overview.compTopicDetail.usedInAds", {
              defaultValue: "Used in {{count}} ads",
              count: formatCompactNumber(row.ads),
            })}
          </span>
        </div>
      </div>

      <div className="flex items-center justify-end gap-1 border-t border-border px-4 py-3">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8"
            disabled={!canGoPrev}
            onClick={() => onSelectedIndexChange(safeIndex - 1)}
            aria-label={t("overview.compTopicDetail.prevCreative", { defaultValue: "Previous creative" })}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8"
            disabled={!canGoNext}
            onClick={() => onSelectedIndexChange(safeIndex + 1)}
            aria-label={t("overview.compTopicDetail.nextCreative", { defaultValue: "Next creative" })}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
      </div>
    </div>
  );
}
